// src/components/hooks/useBuscarEmpresas.ts
import { useState, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

export interface Empresa {
  id_empresa: string;
  empresa: string;
  logo: string | null;
  descripcion?: string | null;
  categoria_slug: string;
  categoria?: string;
  ciudad_slug: string;
  ciudad?: string; 
  empresa_slug: string;
  telefono?: string | null;
  reservas_online_citas?: boolean;
  plan_incluye_reservas?: boolean;
  ultima_reserva_usuario?: string | null;
  valoracion_media_empresa?: number | null;
  total_valoraciones?: number | null;
  latitud?: number | null;
  longitud?: number | null;
  distancia?: number | null;
  imagenes?: string[];
  servicios?: string[];
  subcategorias?: string[];
}

interface PlazasFundador {
  diamante_libres: number;
  platino_libres: number;
}

const RENOTIFY_MS = 30 * 24 * 60 * 60 * 1000; // 30 días

export const useBuscarEmpresas = () => {
  const [empresasBase, setEmpresasBase] = useState<Empresa[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState<Empresa[]>([]);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [plazasFundador, setPlazasFundador] = useState<PlazasFundador | null>(
    null
  );

  // Comprobación inicial ubicación
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    const lastAsked = localStorage.getItem('locationAskedAt');

    if (savedLocation) setUserLocation(JSON.parse(savedLocation));

    if (!lastAsked || Date.now() - Number(lastAsked) > RENOTIFY_MS) {
      setShowLocationDialog(true);
    }
  }, []);

  // Permitir ubicación
  const handleAllowLocation = () => {
    localStorage.setItem('locationAskedAt', Date.now().toString());
    setShowLocationDialog(false);

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
          };
          setUserLocation(coords);
          localStorage.setItem('userLocation', JSON.stringify(coords));
        },
        (err) => {
          Sentry.captureException(err);
          console.warn('Error al obtener ubicación:', err);
        }
      );
    }
  };

  // Pedir la ubicación a petición del usuario (chip "Cerca de mí"), aunque
  // ya se le haya preguntado dentro de la ventana de RENOTIFY_MS.
  const solicitarUbicacion = () => {
    if (userLocation) return;
    setShowLocationDialog(true);
  };

  // Rechazar ubicación
  const handleRejectLocation = () => {
    localStorage.setItem('locationAskedAt', Date.now().toString());
    setShowLocationDialog(false);
  };

  // Cargar plazas de fundador
  const cargarPlazasFundador = async () => {
    const { data, error } = await supabase.rpc('obtener_plazas_fundador');
    if (error) {
      Sentry.captureException(error);
      return;
    }
    if (data && data.length > 0) {
      setPlazasFundador({
        diamante_libres: data[0].diamante_libres,
        platino_libres: data[0].platino_libres,
      });
    }
  };

  useEffect(() => {
    cargarPlazasFundador();
    const onFocus = () => cargarPlazasFundador();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Cargar empresas
  useEffect(() => {
    const fetchEmpresas = async () => {
      const { data, error } = await supabase.rpc('get_empresas_cliente', {
        p_email: null,
      });
      if (error) {
        Sentry.captureException(error);
        return;
      }
      if (!data) return;

      setEmpresasBase(data as Empresa[]);
    };

    fetchEmpresas();
  }, []);

  // Distancia y orden por defecto. Esto era un efecto que reescribía el
  // mismo estado que leía; como memo no puede reentrar y se recalcula solo
  // cuando cambian las empresas o la ubicación.
  const empresas = useMemo(() => {
    const lista = empresasBase.map((e) => ({
      ...e,
      distancia:
        userLocation && e.latitud != null && e.longitud != null
          ? getDistanceKm(userLocation.lat, userLocation.lon, e.latitud, e.longitud)
          : null,
    }));

    lista.sort((a, b) => {
      const aReservada = !!a.ultima_reserva_usuario;
      const bReservada = !!b.ultima_reserva_usuario;
      if (aReservada && !bReservada) return -1;
      if (!aReservada && bReservada) return 1;
      if (a.distancia != null && b.distancia != null)
        return a.distancia - b.distancia;
      return a.empresa.localeCompare(b.empresa);
    });

    return lista;
  }, [empresasBase, userLocation]);

  // Búsqueda en memoria
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFiltered([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = empresas
      .filter((e) => {
        const matchEmpresa = e.empresa?.toLowerCase().includes(term) ?? false;
        const matchServicio =
          e.servicios?.some(
            (s) => s != null && s.toLowerCase().includes(term)
          ) ?? false;
        const matchSubcategoria =
          e.subcategorias?.some(
            (s) => s != null && s.toLowerCase().includes(term)
          ) ?? false;
        const matchDescripcion =
          e.descripcion?.toLowerCase().includes(term) ?? false;
        const matchCategoria =
          e.categoria_slug?.replace(/-/g, ' ').includes(term) ?? false;
        return (
          matchEmpresa ||
          matchServicio ||
          matchSubcategoria ||
          matchDescripcion ||
          matchCategoria
        );
      })
      .slice(0, 8);

    setFiltered(results);
  }, [searchTerm, empresas]);

  return {
    searchTerm,
    setSearchTerm,
    filtered,
    empresas,
    userLocation,
    showLocationDialog,
    solicitarUbicacion,
    handleAllowLocation,
    handleRejectLocation,
    plazasFundador,
    cargarPlazasFundador,
  };
};

// Haversine
const getDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
};

const deg2rad = (deg: number) => deg * (Math.PI / 180);